import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../components/ui/button";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Input } from "../../../../components/ui/input";
import type { FinancialAccessCheck } from "../../../../hooks/useFinancialAccessGuard";
import { formatCurrency } from "../../../../lib/money";
import { getErrorMessage, getProblemDetail, isHttpError } from "../../../../lib/query/errors";
import type { AuditTableColumn } from "../../../audit/AuditTable";
import { useDealerInfo } from "../../api/useDealerInfo";
import SummaryTableModal from "../../components/SummaryTableModal";
import { useSaveCustomerFees } from "./api/useSaveCustomerFees";
import {
  calculateCorrection,
  calculateFeeTotals,
  calculateTotalAdjAmt,
  type CustomerFeeRowData,
  effectiveActual,
  type FeeEntry,
  type FeeViolation,
  parseAmountInput,
  sanitizeAmountInput,
  validateFeeEntries,
} from "./customerFees.logic";
import { EMPTY_VALUE } from "./financialFormat";

interface CustomerFeesModalProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  approvalId: string;
  fees: CustomerFeeRowData[];
  /** `financial.versionToken`, sent as `expectedVersion`; without it nothing can be saved. */
  versionToken?: string;
  readOnly: boolean;
  /** `financial.feeEstimated`; shown (disabled) for California deals only. */
  isFeeEstimated?: boolean;
  /** Contract APR (`financial.tila.apr`) and term (`financial.tila.term`) for Total Adj Amt. */
  apr?: number;
  term?: number;
  /** The state's time-counting method, supplied by the BFF; 2 switches Total Adj Amt to the amortised formula. */
  timeCountingMethod?: number;
  /** Re-evaluates Financial access (spec X2); called before each Apply. */
  checkAccess?: () => Promise<FinancialAccessCheck>;
}

interface FeeRow {
  key: string;
  fee: CustomerFeeRowData;
  editable: boolean;
}

function TotalLine({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div
      role="group"
      aria-label={label}
      className="gap-(--spacing-12) text-(length:--typography-base-sizes-small-font-size)/(--typography-base-sizes-small-line-height) font-(--font-weight-semibold) flex"
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

// Mounted per open (keyed in the wrapper) so every opening starts from the server's values and Cancel discards edits.
function CustomerFeesEditor({
  open,
  onOpenChange,
  approvalId,
  fees,
  versionToken,
  readOnly,
  isFeeEstimated,
  apr,
  term,
  timeCountingMethod,
  checkAccess,
}: CustomerFeesModalProperties) {
  const { t } = useTranslation();
  const canEdit = !readOnly && !!versionToken;

  const rows: FeeRow[] = fees
    .filter((fee) => fee.appliedToDlr !== false)
    .map((fee, index) => ({
      key: String(fee.lineItemPk ?? `row-${index}`),
      fee,
      editable: canEdit && fee.custFeeFlag === true && fee.lineItemPk !== undefined,
    }));

  const [entries, setEntries] = useState<FeeEntry[]>(() =>
    rows.map(({ key, fee }) => {
      const riscAmount = fee.riscAmount ?? fee.actualAmount ?? 0;
      const originalActual = fee.actualAmount ?? riscAmount;
      return { key, feeName: fee.name ?? "", riscAmount, originalActual, input: String(originalActual), edited: false };
    })
  );
  const [attempted, setAttempted] = useState(false);
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);

  // The dealer state drives the MA / CA mutual-exclusion rules; only needed while editing.
  const dealerInfo = useDealerInfo(approvalId, canEdit && open);
  const dealerState = dealerInfo.data?.dealerProfile?.state;
  const saveCustomerFees = useSaveCustomerFees(approvalId);

  const violations = canEdit ? validateFeeEntries(entries, dealerState) : [];
  const totals = calculateFeeTotals(entries);
  const totalAdjAmt = calculateTotalAdjAmt(totals.totalAdjusted, apr, term, timeCountingMethod);

  const message = (violation: FeeViolation): string => {
    switch (violation.kind) {
      case "empty": {
        return t("financial.customerFeesModal.errors.empty", { fee: violation.feeName });
      }
      case "exceeds": {
        return t("financial.customerFeesModal.errors.exceeds", {
          fee: violation.feeName,
          risc: formatCurrency(violation.riscAmount),
        });
      }
      case "registrationExclusive": {
        return t("financial.customerFeesModal.errors.registrationExclusive");
      }
      case "smogExclusive": {
        return t("financial.customerFeesModal.errors.smogExclusive");
      }
    }
  };

  const fieldViolation = (key: string) => violations.find((violation) => "key" in violation && violation.key === key);

  const handleChange = (key: string, raw: string) => {
    setEntries((previous) =>
      previous.map((entry) => (entry.key === key ? { ...entry, input: sanitizeAmountInput(raw), edited: true } : entry))
    );
  };

  const handleSaveError = (error: unknown) => {
    if (isHttpError(error, 409)) {
      setSubmitErrors([t("financial.customerFeesModal.errors.staleVersion")]);
    } else if (isHttpError(error, 403)) {
      setSubmitErrors([t("financial.accessChanged")]);
      void checkAccess?.();
    } else if (isHttpError(error, 422)) {
      const serverMessages = (getProblemDetail(error)?.violations ?? []).map((violation) => violation.message);
      setSubmitErrors(
        serverMessages.length > 0
          ? serverMessages
          : [t("financial.customerFeesModal.errors.saveError", { message: getErrorMessage(error) })]
      );
    } else {
      setSubmitErrors([t("financial.customerFeesModal.errors.saveError", { message: getErrorMessage(error) })]);
    }
  };

  const handleApply = async () => {
    setAttempted(true);
    setSubmitErrors([]);
    // Any violation blocks the whole save: nothing is staged or persisted.
    if (validateFeeEntries(entries, dealerState).length > 0) {
      return;
    }
    if (checkAccess) {
      const access = await checkAccess();
      if (!access.editable) {
        setSubmitErrors([t("financial.accessChanged")]);
        return;
      }
    }

    const corrections = entries.flatMap((entry) => {
      const row = rows.find((candidate) => candidate.key === entry.key);
      const corrected = parseAmountInput(entry.input);
      if (!row?.editable || row.fee.lineItemPk === undefined || corrected === undefined) {
        return [];
      }
      return entry.edited && corrected !== entry.originalActual
        ? [{ lineItemPk: row.fee.lineItemPk, correctedAmount: corrected }]
        : [];
    });
    if (corrections.length === 0 || !versionToken) {
      onOpenChange(false);
      return;
    }

    saveCustomerFees.mutate(
      { expectedVersion: versionToken, fees: corrections },
      { onSuccess: () => onOpenChange(false), onError: handleSaveError }
    );
  };

  const entryFor = (key: string) => entries.find((entry) => entry.key === key);

  const columns: Array<AuditTableColumn<FeeRow>> = [
    { key: "name", header: t("financial.modalColumns.name"), render: ({ fee }) => fee.name || EMPTY_VALUE },
    {
      key: "riscAmount",
      header: t("financial.modalColumns.riscAmount"),
      render: ({ key }) => formatCurrency(entryFor(key)?.riscAmount ?? 0),
    },
    {
      key: "actualAmount",
      header: t("financial.modalColumns.actualAmount"),
      render: ({ key, fee, editable }) => {
        const entry = entryFor(key);
        if (!entry) {
          return EMPTY_VALUE;
        }
        if (!editable) {
          return formatCurrency(effectiveActual(entry));
        }
        const violation = fieldViolation(key);
        return (
          <div className="flex flex-col">
            <Input
              inputMode="decimal"
              value={entry.input}
              aria-label={t("financial.customerFeesModal.actualAmountFor", { fee: fee.name })}
              aria-invalid={violation ? true : undefined}
              onChange={(event) => handleChange(key, event.target.value)}
            />
            {violation && (
              <span role="alert" className="text-destructive text-[10px]">
                {message(violation)}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "correctionAmount",
      header: t("financial.modalColumns.correctionAmount"),
      render: ({ key }) => {
        const entry = entryFor(key);
        return entry ? formatCurrency(calculateCorrection(entry.riscAmount, effectiveActual(entry))) : EMPTY_VALUE;
      },
    },
    {
      key: "vendorName",
      header: t("financial.modalColumns.vendorName"),
      render: ({ fee }) => fee.vendorName || EMPTY_VALUE,
    },
    {
      key: "lineNumber",
      header: t("financial.modalColumns.lineNumber"),
      render: ({ fee }) => fee.contractLineNumber || EMPTY_VALUE,
    },
  ];

  const listedErrors = [...(attempted ? violations.map((violation) => message(violation)) : []), ...submitErrors];

  return (
    <SummaryTableModal
      open={open}
      onOpenChange={onOpenChange}
      title={t("financial.customerFeesModal.title")}
      description={t("financial.customerFeesModal.description")}
      caption={t("financial.customerFeesModal.caption")}
      contentClassName="sm:max-w-3xl"
      columns={columns}
      rows={rows}
      topContent={
        listedErrors.length > 0 && (
          <ul role="alert" className="text-destructive text-xs">
            {listedErrors.map((error) => (
              <li key={error}>* {error}</li>
            ))}
          </ul>
        )
      }
      bottomContent={
        <div className="gap-(--spacing-2) flex flex-col">
          <TotalLine
            label={t("financial.customerFeesModal.totalCustomerFee")}
            value={formatCurrency(totals.totalFee)}
          />
          <TotalLine label={t("financial.customerFeesModal.totalActual")} value={formatCurrency(totals.totalActual)} />
          <TotalLine label={t("financial.customerFeesModal.totalAdjusted")} value={formatCurrency(totalAdjAmt)} />
          {dealerState?.toUpperCase() === "CA" && (
            <label className="gap-(--spacing-2) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) flex cursor-not-allowed items-center">
              <Checkbox className="bg-white" checked={isFeeEstimated === true} disabled />
              <span>{t("financial.customerFeesModal.estimatedFees")}</span>
            </label>
          )}
        </div>
      }
      footerActions={
        canEdit && (
          <Button
            type="button"
            size="xs"
            disabled={saveCustomerFees.isPending || dealerInfo.isLoading}
            onClick={() => void handleApply()}
          >
            {saveCustomerFees.isPending && <Loader2 aria-hidden="true" className="size-3 animate-spin" />}
            {t("financial.customerFeesModal.apply")}
          </Button>
        )
      }
    />
  );
}

// Opened from the "Total Customer Fees" link; editable Actual Amounts when access allows, otherwise a read-only breakdown.
function CustomerFeesModal(properties: CustomerFeesModalProperties) {
  return <CustomerFeesEditor key={String(properties.open)} {...properties} />;
}

export default CustomerFeesModal;
